import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { translateInto, translateSubtree } from '@theme/utilities';

const selectors = {
  PRICE: '[ref="priceContainer"]',
  BADGE: '[ref="badge"]',
};

/**
 * Product card with ref-based DOM updates.
 * Listens for `linked-color:change` events from child swatches
 * to swap images, title, price, badge, and links.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} figureLink
 * @property {HTMLElement} [figure]
 * @property {HTMLAnchorElement} [titleLink]
 *
 * @extends {Component<Refs>}
 */
export class ProductCardNative extends Component {
  requiredRefs = ['figureLink'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(ThemeEvents.linkedColorChange, this.#handleLinkedColorChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(ThemeEvents.linkedColorChange, this.#handleLinkedColorChange);
  }

  /** @param {CustomEvent} event */
  #handleLinkedColorChange = (event) => {
    const { detail } = event;
    if (!detail) return;

    this.#updateLinks(detail);
    this.#updateImages(detail);
    this.#updateTitle(detail);
    this.#updatePrice(detail);
    this.#updateBadge(detail);
    this.#updateQuickAdd(detail);
  };

  /** @param {Record<string, string>} detail */
  #updateLinks(detail) {
    const { figureLink, titleLink } = this.refs;
    figureLink.href = detail.productUrl ?? '';
    figureLink.title = detail.productTitle ?? '';
    if (titleLink) titleLink.href = detail.productUrl ?? '';
  }

  /** @param {Record<string, string>} detail */
  async #updateImages(detail) {
    const { figure } = this.refs;
    if (!figure || !detail.figureHtml) return;

    const temp = Object.assign(document.createElement('div'), {
      innerHTML: detail.figureHtml,
    });

    await Promise.all([...temp.querySelectorAll('img')].map((img) => img.decode().catch(() => {})));
    figure.replaceChildren(...temp.childNodes);
  }

  /** @param {Record<string, string>} detail */
  #updateTitle(detail) {
    const { titleLink } = this.refs;
    if (!titleLink) return;

    // Old translated title stays visible until this resolves — no flash of source text.
    translateInto(titleLink, detail.productTitle ?? '');
  }

  /** @param {Record<string, string>} detail */
  #updatePrice(detail) {
    if (!detail.priceHtml) return;

    const el = this.querySelector(selectors.PRICE);
    if (!el) return;

    el.outerHTML = detail.priceHtml;
    // Re-query the injected source-locale node and translate it.
    const newEl = this.querySelector(selectors.PRICE);
    if (newEl) translateSubtree(newEl);
  }

  /** @param {Record<string, string>} detail */
  #updateBadge(detail) {
    if (!detail.badgeHtml) return;

    const el = this.querySelector(selectors.BADGE);
    if (!el) return;

    el.outerHTML = detail.badgeHtml;
    const newEl = this.querySelector(selectors.BADGE);
    if (newEl) translateSubtree(newEl);
  }

  /** @param {Record<string, string>} detail */
  #updateQuickAdd(detail) {
    const quickAddEl = this.querySelector('quick-add-component');
    if (!quickAddEl) return;

    const handle = detail.productHandle ?? '';
    if (handle) quickAddEl.dataset.productHandle = handle;
    if (detail.productTitle) quickAddEl.dataset.productTitle = detail.productTitle;
    delete quickAddEl.dataset.productVariant;
  }
}

if (!customElements.get('product-card-native')) {
  customElements.define('product-card-native', ProductCardNative);
}
